import React, { useEffect, useState, useCallback, useMemo } from "react";
import { apiGet } from "../../services/api";
import "../styles/PageBuilder.css";
import { useParams } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import NotFound from "../../user_panel/pages/NotFound";

// Функция для получения текста на нужном языке с мемоизацией
const getTextByLanguage = (textObj, preferredLang) => {
  if (!textObj || typeof textObj !== "object") return textObj || "";

  // Приоритет: предпочитаемый язык -> ru -> kz -> en -> первый доступный
  return (
    textObj[preferredLang] ||
    textObj.ru ||
    textObj.kz ||
    textObj.en ||
    Object.values(textObj)[0] ||
    ""
  );
};
const HTMLContent = ({ content, className = "", language = "kz" }) => {
  const htmlContent = getTextByLanguage(content, language);

  if (!htmlContent) return null;

  return (
    <div
      className={`html-content ${className}`}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

// Компоненты блоков с React.memo для оптимизации
const TitleBlock = ({ content, language = "kz" }) => (
  <div className="title-block">
    <HTMLContent
      content={content.title}
      language={language}
      className="title-main"
    />
    {content.subtitle && (
      <HTMLContent
        content={content.subtitle}
        language={language}
        className="title-subtitle"
      />
    )}
  </div>
);

const TextBlock = React.memo(({ content, language }) => {
  const textContent = useMemo(
    () => getTextByLanguage(content.text, language),
    [content.text, language]
  );

  return (
    <div className="text-block">
      <div
        dangerouslySetInnerHTML={{
          __html: textContent,
        }}
      />
    </div>
  );
});

const ImageBlock = React.memo(({ content }) => (
  <div className="image-block">
    {content.images &&
      content.images.map((image, index) => (
        <img
          key={`${image.imageUrl}-${index}`}
          src={`${process.env.REACT_APP_API_URL}${image.imageUrl}`}
          alt={image.altText || `Image ${index + 1}`}
          loading="lazy" // Добавляем lazy loading
          onError={(e) => {
            e.target.style.display = "none"; // Скрываем сломанные изображения
          }}
        />
      ))}
  </div>
));

const TextImageBlock = React.memo(({ content, language }) => {
  const textContent = useMemo(
    () => getTextByLanguage(content.text, language),
    [content.text, language]
  );

  const altText = useMemo(
    () => getTextByLanguage(content.altText, language) || "Image",
    [content.altText, language]
  );

  return (
    <div className={`text-image-block ${content.imagePosition || "right"}`}>
      <div className="text-content">
        <div
          dangerouslySetInnerHTML={{
            __html: textContent,
          }}
        />
      </div>
      <div className="image-content">
        <img
          src={`${process.env.REACT_APP_API_URL}${content.imageUrl}`}
          alt={altText}
          loading="lazy"
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
      </div>
    </div>
  );
});

const LinksBlock = React.memo(({ content, language }) => {
  const titleContent = useMemo(
    () => getTextByLanguage(content.title, language),
    [content.title, language]
  );

  const handleRowClick = useCallback((url) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  if (content.displayStyle === "table") {
    return (
      <div className="links-block table-style">
        <div
          dangerouslySetInnerHTML={{
            __html: titleContent,
          }}
        />
        <table className="links-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Ссылка</th>
            </tr>
          </thead>
          <tbody>
            {content.links &&
              content.links.map((link, index) => (
                <tr
                  key={index}
                  onClick={() => handleRowClick(getTextByLanguage(link.url, language))}
                  className="clickable-row"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleRowClick(getTextByLanguage(link.url, language));
                    }
                  }}
                >
                  <td>{index + 1}</td>
                  <td>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: getTextByLanguage(link.text, language),
                      }}
                    />
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Обычный стиль (по умолчанию)
  return (
    <div className="links-block">
      <div
        dangerouslySetInnerHTML={{
          __html: titleContent,
        }}
      />
      <ul>
        {content.links &&
          content.links.map((link, index) => (
            <li key={index}>
              <a
                href={getTextByLanguage(link.url, language)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`External link: ${getTextByLanguage(
                  link.text,
                  language
                )}`}
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: getTextByLanguage(link.text, language),
                  }}
                />
              </a>
            </li>
          ))}
      </ul>
    </div>
  );
});

// Рендер блока по его типу с мемоизацией
const BlockRenderer = React.memo(({ block, language }) => {
  if (!block || !block.content) {
    return <div className="block-error">Ошибка: неверные данные блока</div>;
  }

  switch (block.type) {
    case "TitleBlock":
      return <TitleBlock content={block.content} language={language} />;
    case "TextBlock":
      return <TextBlock content={block.content} language={language} />;
    case "ImageBlock":
      return <ImageBlock content={block.content} />;
    case "TextImageBlock":
      return <TextImageBlock content={block.content} language={language} />;
    case "LinksBlock":
      return <LinksBlock content={block.content} language={language} />;
    default:
      return (
        <div className="block-error">Неизвестный тип блока: {block.type}</div>
      );
  }
});

// Компонент ошибки загрузки с возможностью повтора
const PageError = React.memo(({ onRetry, errorMessage }) => {
  const { currentLanguage } = useLanguage();

  const errorTexts = useMemo(
    () => ({
      ru: {
        title: "Ошибка загрузки страницы",
        subtitle:
          "Произошла ошибка при загрузке содержимого страницы. Попробуйте обновить страницу или вернуться позже.",
        retryButton: "🔄 Попробовать снова",
        homeButton: "🏠 На главную",
      },
      kz: {
        title: "Бетті жүктеу қатесі",
        subtitle:
          "Бет мазмұнын жүктеу кезінде қате пайда болды. Бетті жаңартып көріңіз немесе кейінірек қайта келіңіз.",
        retryButton: "🔄 Қайта көру",
        homeButton: "🏠 Басты бетке",
      },
      en: {
        title: "Page Loading Error",
        subtitle:
          "An error occurred while loading the page content. Please try refreshing the page or come back later.",
        retryButton: "🔄 Try Again",
        homeButton: "🏠 Home",
      },
    }),
    []
  );

  const t = errorTexts[currentLanguage] || errorTexts.ru;

  const handleHomeClick = useCallback(() => {
    window.location.href = "/";
  }, []);

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{
        backgroundColor: "var(--background-main)",
        fontFamily: "var(--font-family-main)",
      }}
    >
      <div
        className="text-center max-w-md w-full p-8 rounded-lg shadow-lg"
        style={{
          backgroundColor: "var(--background-light)",
          borderRadius: "var(--border-radius-lg)",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div className="text-6xl mb-4" role="img" aria-label="Warning">
          ⚠️
        </div>

        <h1
          className="text-2xl font-bold mb-4"
          style={{
            color: "var(--text-dark)",
            fontSize: "var(--font-size-xl)",
          }}
        >
          {t.title}
        </h1>

        {errorMessage && (
          <div
            className="mb-4 p-3 rounded-lg"
            style={{
              backgroundColor: "rgba(239, 68, 68, 0.1)",
              color: "#dc2626",
              fontSize: "var(--font-size-sm)",
              borderRadius: "var(--border-radius-md)",
            }}
            role="alert"
          >
            {errorMessage}
          </div>
        )}

        <p
          className="mb-6 leading-relaxed"
          style={{
            color: "var(--text-dark)",
            fontSize: "var(--font-size-md)",
            opacity: "0.8",
          }}
        >
          {t.subtitle}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={onRetry}
            className="px-6 py-3 rounded-lg font-semibold transform hover:-translate-y-1 transition-all duration-300"
            style={{
              backgroundColor: "var(--primary-color)",
              color: "var(--text-light)",
              borderRadius: "var(--border-radius-md)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--primary-light)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--primary-color)";
            }}
          >
            {t.retryButton}
          </button>

          <button
            onClick={handleHomeClick}
            className="border-2 px-6 py-3 rounded-lg font-semibold transform hover:-translate-y-1 transition-all duration-300"
            style={{
              borderColor: "var(--primary-color)",
              color: "var(--primary-color)",
              borderRadius: "var(--border-radius-md)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--primary-color)";
              e.target.style.color = "var(--text-light)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "transparent";
              e.target.style.color = "var(--primary-color)";
            }}
          >
            {t.homeButton}
          </button>
        </div>
      </div>
    </div>
  );
});

// Главный компонент страницы
const PageBuilder = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isNotFound, setIsNotFound] = useState(false);
  const { currentLanguage } = useLanguage();
  const { slug } = useParams();

  // Мемоизируем функцию загрузки страницы
  const loadPage = useCallback(async () => {
    setLoading(true);
    setError("");
    setIsNotFound(false);

    try {
      const res = await apiGet(
        `${process.env.REACT_APP_API_URL}/api/block/page/${slug}/full`,
        {
          timeoutMs: 10000, // Добавляем таймаут в 10 секунд
        }
      );
      setPageData(res.data);
    } catch (error) {
      console.error("Ошибка загрузки страницы:", error);

      if (error.response?.status === 404) {
        setIsNotFound(true);
      } else if (error.code === "ECONNABORTED") {
        setError("Превышено время ожидания загрузки страницы");
      } else {
        setError(
          error.response?.data?.message ||
            error.message ||
            "Ошибка загрузки страницы"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  // Мемоизируем тексты загрузки
  const loadingTexts = useMemo(
    () => ({
      ru: "Загрузка...",
      kz: "Жүктелуде...",
      en: "Loading...",
    }),
    []
  );

  const notFoundTexts = useMemo(
    () => ({
      ru: "Страница не найдена",
      kz: "Бет табылмады",
      en: "Page not found",
    }),
    []
  );

  const emptyPageTexts = useMemo(
    () => ({
      kz: "Бетте әзірше мазмұн жоқ",
      en: "No content on this page yet",
      ru: "На странице пока нет контента",
    }),
    []
  );

  // Мемоизируем отсортированные блоки
  const sortedBlocks = useMemo(() => {
    return pageData?.blocks
      ? [...pageData.blocks].sort((a, b) => a.order - b.order)
      : [];
  }, [pageData?.blocks]);

  // Мемоизируем заголовок страницы
  const pageTitle = useMemo(() => {
    return getTextByLanguage(pageData?.titles, currentLanguage);
  }, [pageData?.titles, currentLanguage]);

  // Показываем загрузку
  if (loading) {
    return (
      <div
        className="page-builder"
        style={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-family-main)",
        }}
      >
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 border-4 border-opacity-30 mx-auto mb-4"
            style={{
              borderColor: "var(--primary-color)",
              borderTopColor: "var(--secondary-color)",
            }}
            role="status"
            aria-label="Loading"
          ></div>
          <div
            className="loading-message"
            style={{
              color: "var(--text-dark)",
              fontSize: "var(--font-size-lg)",
            }}
          >
            {loadingTexts[currentLanguage] || loadingTexts.ru}
          </div>
        </div>
      </div>
    );
  }

  // Показываем 404 если страница не найдена
  if (isNotFound) {
    return <NotFound />;
  }

  // Показываем ошибку с возможностью повтора
  if (error) {
    return <PageError onRetry={loadPage} errorMessage={error} />;
  }

  // Если нет данных (но не 404)
  if (!pageData) {
    return (
      <div
        className="page-builder"
        style={{
          minHeight: "50vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily: "var(--font-family-main)",
        }}
      >
        <div
          className="not-found-message text-center"
          style={{
            color: "var(--text-dark)",
            fontSize: "var(--font-size-lg)",
          }}
        >
          {notFoundTexts[currentLanguage] || notFoundTexts.ru}
        </div>
      </div>
    );
  }

  return (
    <div
      className="page-builder"
      style={{ fontFamily: "var(--font-family-main)" }}
    >
      <div className="blocks-container">
        {sortedBlocks.length > 0 ? (
          sortedBlocks.map((block) => (
            <BlockRenderer
              key={block.id}
              block={block}
              language={currentLanguage}
            />
          ))
        ) : (
          <div
            className="empty-page-message text-center"
            style={{
              padding: "var(--spacing-xl)",
              color: "var(--text-dark)",
              fontSize: "var(--font-size-md)",
            }}
          >
            {emptyPageTexts[currentLanguage] || emptyPageTexts.ru}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageBuilder;
